import React, { useEffect, useRef, useState } from 'react';
import { Calendar, CheckCircle, Clock, MapPin, Users, DollarSign, Gift, Camera, Music, Utensils, Flower, Palette, Sparkles, ArrowRight, Heart, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const Dashboard = () => {
  const { user } = useAuth();
  const sparklesRef = useRef<HTMLDivElement>(null);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [tasks, setTasks] = useState<Record<number, Array<{id: number, title: string, completed: boolean}>>>({});
  const [weddingDate, setWeddingDate] = useState<string | null>(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [attendingGuestsCount, setAttendingGuestsCount] = useState(0);
  const [totalTasks] = useState(15);

  useEffect(() => {
    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Initialize tasks
    setTasks({
      1: [
        { id: 1, title: 'Set your wedding date', completed: false },
        { id: 2, title: 'Determine your budget', completed: false },
        { id: 3, title: 'Create your guest list', completed: false },
        { id: 4, title: 'Start venue research', completed: false }
      ],
      2: [
        { id: 5, title: 'Book your venue', completed: false },
        { id: 6, title: 'Hire key vendors', completed: false },
        { id: 7, title: 'Order your dress', completed: false },
        { id: 8, title: 'Plan engagement party', completed: false }
      ],
      3: [
        { id: 9, title: 'Send invitations', completed: false },
        { id: 10, title: 'Plan menu and tastings', completed: false },
        { id: 11, title: 'Choose flowers and decor', completed: false },
        { id: 12, title: 'Book honeymoon', completed: false }
      ],
      4: [
        { id: 13, title: 'Final headcount', completed: false },
        { id: 14, title: 'Rehearsal dinner', completed: false },
        { id: 15, title: 'Day-of timeline', completed: false }
      ]
    });

    // Load wedding date from registry
    loadWeddingDate();
    fetchAttendingGuestsCount();
  }, []);

  useEffect(() => {
    if (weddingDate) {
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [weddingDate]);

  const loadWeddingDate = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('registries')
        .select('event_date')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data) {
        setWeddingDate(data.event_date);
      }
    } catch (error) {
      console.error('Error loading wedding date:', error);
    }
  };

  const fetchAttendingGuestsCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get all registries owned by the user
      const { data: registries, error: registriesError } = await supabase
        .from('registries')
        .select('id')
        .eq('user_id', user.id);

      if (registriesError) throw registriesError;
      if (!registries || registries.length === 0) return;

      const registryIds = registries.map(r => r.id);

      // Count attending guests across all registries
      const { count, error: rsvpError } = await supabase
        .from('registry_rsvps')
        .select('*', { count: 'exact', head: true })
        .in('registry_id', registryIds)
        .eq('status', 'attending');

      if (rsvpError) throw rsvpError;
      setAttendingGuestsCount(count || 0);
    } catch (error) {
      console.error('Error fetching attending guests count:', error);
    }
  };

  const updateCountdown = () => {
    if (!weddingDate) return;

    const now = new Date().getTime();
    const weddingTime = new Date(weddingDate).getTime();
    const difference = weddingTime - now;

    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    } else {
      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }
  };

  const toggleTask = (phaseId: number, taskId: number) => {
    setTasks(prev => ({
      ...prev,
      [phaseId]: prev[phaseId].map(task => 
        task.id === taskId ? { ...task, completed: !task.completed } : task
      )
    }));
  };

  const completedTasks = Object.values(tasks).flat().filter(task => task.completed).length;

  const phases = [
    {
      id: 1,
      title: '12+ Months',
      subtitle: 'Dream & Plan',
      description: 'Set your vision and start the journey',
      color: 'bg-pink-100 text-pink-800 border-pink-200',
      activeColor: 'bg-pink-500 text-white'
    },
    {
      id: 2,
      title: '12-5 Months',
      subtitle: 'Book & Budget',
      description: 'Secure your venue and vendors',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      activeColor: 'bg-blue-500 text-white'
    },
    {
      id: 3,
      title: '7-3 months',
      subtitle: 'Details & Design',
      description: 'Plan the details and send invitations',
      color: 'bg-green-100 text-green-800 border-green-200',
      activeColor: 'bg-green-500 text-white'
    },
    {
      id: 4,
      title: '2-0 months',
      subtitle: 'Final Touches',
      description: 'Finalize everything for the big day',
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      activeColor: 'bg-purple-500 text-white'
    }
  ];

  const quickActions = [
    {
      title: 'Find Venues',
      description: 'Browse beautiful wedding venues',
      icon: MapPin,
      link: '/venues',
      color: 'bg-pink-50 hover:bg-pink-100 border-pink-200',
      iconColor: 'text-pink-600'
    },
    {
      title: 'Find Vendors',
      description: 'Discover trusted wedding professionals',
      icon: Camera,
      link: '/vendors',
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Manage Guests',
      description: 'Create your guest list and send invitations',
      icon: Users,
      link: '/create',
      color: 'bg-green-50 hover:bg-green-100 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Plan Budget',
      description: 'Track expenses and stay on budget',
      icon: DollarSign,
      link: '/budget',
      color: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200',
      iconColor: 'text-yellow-600'
    },
    {
      title: 'Gift Registry',
      description: 'Create and manage your wedding registry',
      icon: Gift,
      link: '/create',
      color: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Day-of Planning',
      description: 'Organize seating and final details',
      icon: Calendar,
      link: '/day-of-planning',
      color: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200',
      iconColor: 'text-indigo-600'
    }
  ];

  const progressPercentage = (completedTasks / totalTasks) * 100;

  return (
    <div className="max-w-7xl mx-auto space-y-12">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      {/* Welcome Header */}
      <div className="text-center space-y-6 animate-fade-in">
        <h1 className="text-4xl md:text-5xl font-display font-bold text-white text-shadow-lg">
          Welcome to Your
          <span className="block text-gradient">Wedding Journey</span>
        </h1>
        <p className="text-xl text-white/70 max-w-3xl mx-auto font-body leading-relaxed">
          Let's transform your dream wedding into reality. Our comprehensive platform guides you through every elegant detail of your special day.
        </p>
      </div>

      {/* Wedding Countdown */}
      <div className="bg-gradient-to-br from-pastel-purple-400/30 via-pastel-pink-400/30 to-pastel-blue-400/30 backdrop-blur-xl border border-white/30 rounded-3xl shadow-glow-lg p-12 text-center animate-fade-in relative overflow-hidden">
        {/* Floating celebration elements */}
        <div className="absolute top-4 left-4 w-3 h-3 bg-pastel-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-4 right-4 w-3 h-3 bg-pastel-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-4 left-4 w-3 h-3 bg-pastel-green-400 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-4 right-4 w-3 h-3 bg-pastel-blue-400 rounded-full animate-bounce" style={{ animationDelay: '1.5s' }}></div>
        
        <h2 className="text-3xl font-display font-bold text-white mb-2 animate-pulse">🎉 Your Big Day Countdown 🎉</h2>
        <p className="text-white/80 mb-8 font-body animate-fade-in">The magic is almost here!</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-lg mx-auto">
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-3xl font-bold text-white animate-pulse">{countdown.days}</p>
            <p className="text-sm text-white/70 font-body">Days</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-3xl font-bold text-white animate-pulse" style={{ animationDelay: '0.2s' }}>{countdown.hours}</p>
            <p className="text-sm text-white/70 font-body">Hours</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-3xl font-bold text-white animate-pulse" style={{ animationDelay: '0.4s' }}>{countdown.minutes}</p>
            <p className="text-sm text-white/70 font-body">Minutes</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-3xl font-bold text-white animate-pulse" style={{ animationDelay: '0.6s' }}>{countdown.seconds}</p>
            <p className="text-sm text-white/70 font-body">Seconds</p>
          </div>
        </div>
        <p className="mt-8 text-white/90 font-body text-lg animate-bounce" style={{ animationDelay: '2s' }}>
          {weddingDate ? 
            `✨ Your magical day is ${new Date(weddingDate).toLocaleDateString()}! ✨` : 
            '🎊 Create a registry to set your wedding date and start the countdown! 🎊'
          }
        </p>
      </div>

      {/* Progress Overview */}
      <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-8 animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-display font-bold text-white">Your Wedding Plan</h2>
          <div className="text-right">
            <p className="text-3xl font-bold text-luxury-gold-400">{completedTasks}/{totalTasks}</p>
            <p className="text-sm text-white/60 font-body">tasks completed</p>
          </div>
        </div>
        
        <div className="w-full bg-white/10 rounded-full h-4 mb-6 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-luxury-gold-400 to-luxury-gold-600 h-4 rounded-full transition-all duration-500 shadow-glow"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        <p className="text-white/80 text-center font-body">
          {progressPercentage < 25 ? "Just getting started - you've got this! 🌟" :
           progressPercentage < 50 ? "Great progress! Keep up the momentum! 🎉" :
           progressPercentage < 75 ? "You're doing amazing! Almost there! 💪" :
           "Wow! You're so close to your perfect day! ✨"}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
        {/* Timeline Section */}
        <div className="lg:col-span-1">
          <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-8">
            <h3 className="text-xl font-display font-bold text-white mb-8">Planning Timeline</h3>
            
            <div className="space-y-4">
              {phases.map((phase, index) => (
                <button
                  key={phase.id}
                  onClick={() => setCurrentPhase(phase.id)}
                  className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 ${
                    currentPhase === phase.id 
                      ? 'bg-luxury-gold-500/20 border-luxury-gold-500/50 text-white shadow-glow transform scale-105' 
                      : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                      currentPhase === phase.id ? 'bg-luxury-gold-500 text-white' : 'bg-white/20 text-white/70'
                    }`}>
                      {phase.id}
                    </div>
                    <div>
                      <p className="font-display font-semibold">{phase.title}</p>
                      <p className="text-sm opacity-80 font-body">{phase.subtitle}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Current Phase Tasks */}
        <div className="lg:col-span-2">
          <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-display font-bold text-white">
                {phases.find(p => p.id === currentPhase)?.subtitle} Tasks
              </h3>
              <span className="px-4 py-2 bg-luxury-gold-500/20 border border-luxury-gold-500/30 text-luxury-gold-400 rounded-full text-sm font-medium backdrop-blur-sm">
                Phase {currentPhase}
              </span>
            </div>

            <div className="space-y-3">
              {tasks[currentPhase]?.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center space-x-4 p-4 rounded-xl hover:bg-white/5 transition-all duration-300"
                >
                  <button
                    onClick={() => {
                      toggleTask(currentPhase, task.id);
                    }}
                    className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      task.completed 
                        ? 'bg-luxury-gold-500 border-luxury-gold-500 text-white shadow-glow' 
                        : 'border-white/30 hover:border-luxury-gold-400 text-white/70'
                    }`}
                  >
                    {task.completed && <CheckCircle className="w-5 h-5" />}
                  </button>
                  <span className={`flex-1 font-body ${task.completed ? 'line-through text-white/50' : 'text-white'}`}>
                    {task.title}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-luxury-gold-500/10 border border-luxury-gold-500/20 rounded-xl backdrop-blur-sm">
              <p className="text-luxury-gold-200 text-sm text-center font-body">
                <Sparkles className="w-4 h-4 inline mr-2" />
                <strong>Tip:</strong> {phases.find(p => p.id === currentPhase)?.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div className="space-y-6">
        <h2 className="text-3xl font-display font-bold text-white text-center">Premium Planning Tools</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              className="group bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg overflow-hidden hover:scale-105 transition-all duration-500 animate-fade-in"
              className="group card-luxury p-8 hover:scale-105 transition-all duration-500 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start space-x-6">
                <div className="w-14 h-14 bg-gradient-to-br from-luxury-gold-400 to-luxury-gold-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <action.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-white text-lg mb-2">{action.title}</h3>
                  <p className="text-white/70 font-body leading-relaxed">{action.description}</p>
                  <div className="flex items-center mt-4 text-luxury-gold-400 group-hover:translate-x-2 transition-transform duration-300">
                    <span className="text-sm font-medium">Explore</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Motivational Message */}
      <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-8 text-center animate-fade-in">
        <Heart className="w-16 h-16 mx-auto mb-6 text-luxury-gold-400" />
        <h3 className="text-2xl font-display font-semibold text-white mb-4">
          You're Creating Something Beautiful
        </h3>
        <p className="text-white/70 font-body text-lg leading-relaxed max-w-2xl mx-auto">
          Take it one elegant step at a time. Every extraordinary wedding begins with thoughtful planning. 
          You're crafting memories that will last a lifetime.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;